// "View calls" (2026-10-27; docs/contact-view.md): one small link per step,
// "View call" for one call or "View calls (N)" for several, in place of a
// row of "View call: <label>" links. It opens one modal listing each call
// by its label, collapsed (a lone call opens expanded); each expands to its endpoint, method, headers
// and body (ApiCallDetails, below; it was in ApiCallModal.jsx until that lone-call
// modal was deleted, 2026-10-28), and "Expand
// all" opens them all. Used by the agent draft panel (A2), the pipeline
// layout, the shadow panel, Other Actions (its steps and Cancel Order) and
// the Activity Log (2026-10-27). Each caller keeps its own visibility:
// every one of them renders it only for an admin.
//
// Wide and draggable (2026-10-29): the modal is the app's DraggableModal
// (react-rnd: moved by its header, resized from any edge or corner,
// non-blocking), opening at 80% of the viewport (640 to 1400px wide) and
// centered; the person's last size is kept (viewCalls.js). The trigger is
// one shared component: a primary-color link with a code icon on a step
// (ViewCalls), an outlined "View N API calls" button in the Activity Log
// (variant="button"), and an Activity Log row with detail opens the modal
// at that call (useViewCallsModal + ViewCallRow). Each collapsed row shows
// a recorded call's found / not found / error chip (CallStatusChip,
// 2026-10-29), the same as its Activity Log row.
import { useEffect, useRef, useState } from 'react'
import { Box, Button, ButtonBase, Link, Stack, SvgIcon, Typography } from '@mui/material'
import DraggableModal from './DraggableModal.jsx'
import { CopyButton } from './CopyButton.jsx'
import { WRAP } from './AgentDraftLayout.jsx'
import CallStatusChip from './CallStatusChip.jsx'
import {
  viewCallsLinkText, activityViewCallsText, viewCallEntries, initialExpanded, expandedForIndex, toggleExpanded,
  toggleExpandAll, expandAllText, VIEW_CALLS_MIN, defaultModalSize, loadModalSize, saveModalSize, clearModalSize, blockText,
} from './viewCalls.js'

// The "code" glyph (feather's code: </>), drawn here because
// @mui/icons-material is not a dependency (src/icons.jsx).
export function CodeIcon({ sx, ...props }) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24" aria-hidden
      sx={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2.25, strokeLinecap: 'round', strokeLinejoin: 'round', ...sx }}>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </SvgIcon>
  )
}

// localStorage itself can throw on access (blocked site data).
function storage() {
  try { return window.localStorage } catch { return null }
}

// One call's endpoint, method, headers and body. `call` is one action's
// `apiCall` (or a plannedSteps[] entry's `apiCall`): { service, method, url,
// body }. Headers show only when the call carries them, already masked by
// the server; otherwise the auth-headers line says they are added at call
// time. A call the Activity Log recorded (`logged`, 2026-10-27) is the
// call as it was made: { endpoint, headers, requestBody, response,
// errorBody }; it also shows its response and error detail, and never the
// "added at call time" line. Each block takes the modal's full width,
// wraps long lines, scrolls inside itself past blockMaxHeight and has a
// copy button (2026-10-29).
const preSx = (maxHeight) => ({
  p: 1.5, pr: 4.5, bgcolor: 'action.hover', borderRadius: 1,
  fontFamily: 'monospace', fontSize: '0.8rem',
  m: 0, overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word',
  ...(maxHeight ? { maxHeight } : {}),
})

function Block({ title, text, testId, maxHeight, children }) {
  return (
    <Box data-testid={testId} data-view-calls-block={title} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{title}</Typography>
      <Box sx={{ position: 'relative', mt: 0.5 }}>
        <Box component="pre" data-testid="view-calls-block-pre" sx={preSx(maxHeight)}>
          {children ?? text}
        </Box>
        <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
          <CopyButton value={text} ariaLabel={`copy ${title.toLowerCase()}`} />
        </Box>
      </Box>
    </Box>
  )
}

export function ApiCallDetails({ call, logged = false, blockMaxHeight = null }) {
  const url = call?.url ?? call?.endpoint
  const headers = call?.headers && typeof call.headers === 'object' && Object.keys(call.headers).length ? call.headers : null
  const body = call?.body ?? call?.requestBody
  const endpoint = url ?? call?.label ?? ''
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Block title="Endpoint" text={[call?.method, endpoint].filter(Boolean).join(' ')} maxHeight={blockMaxHeight}>
        {call?.method && (
          <Box component="span" sx={{
            px: 0.75, py: 0.2, mr: 1, borderRadius: 0.5, fontWeight: 700, fontSize: '0.7rem',
            bgcolor: call.method === 'DELETE' ? 'error.main' : call.method === 'POST' ? 'success.main' : 'info.main',
            color: '#fff',
          }}>
            {call.method}
          </Box>
        )}
        {endpoint}
      </Block>
      {headers && <Block title="Headers" text={blockText(headers)} maxHeight={blockMaxHeight} />}
      {body && <Block title="Request" text={blockText(body)} maxHeight={blockMaxHeight} />}
      {logged && call?.response !== undefined && <Block title="Response" text={blockText(call.response)} testId="api-call-response" maxHeight={blockMaxHeight} />}
      {logged && call?.errorBody && <Block title="Error Detail" text={blockText(call.errorBody)} testId="api-call-error-detail" maxHeight={blockMaxHeight} />}
      {!headers && !logged && (
        <Typography variant="caption" color="text.secondary">
          Auth headers (Authorization, X-Preferred-Role, Content-Type) are added at call time and not shown here.
        </Typography>
      )}
    </Stack>
  )
}

const CLOSED = { open: false, expanded: new Set(), focus: null, seq: 0, size: null, resetKey: 0 }

// The modal's state for one list of calls: openAt(index) opens it with that
// call expanded and scrolled into view (an Activity Log row); openAt() opens
// it the usual way (a lone call expanded, several collapsed).
export function useViewCallsModal(list) {
  const [state, setState] = useState(CLOSED)
  const openAt = (index = null) => setState((s) => ({
    ...s,
    open: true,
    expanded: index === null ? initialExpanded(list.length) : expandedForIndex(list.length, index),
    focus: index,
    seq: s.seq + 1,
    size: loadModalSize(storage(), window.innerWidth, window.innerHeight),
  }))
  const close = () => setState((s) => ({ ...s, open: false, expanded: new Set(), focus: null }))
  const modal = <ViewCallsModal list={list} state={state} setState={setState} onClose={close} />
  return { openAt, close, modal }
}

// Escape closes only the newest View calls window, and not the window it
// was opened from (Other Actions is a DraggableModal too, and every
// DraggableModal closes on Escape): a capture listener on window runs before
// those, and the newest open View calls stops the key there. The MUI Dialog
// this replaced did the same by stopping the key.
const escapeStack = []

function ViewCallsModal({ list, state, setState, onClose }) {
  const itemRefs = useRef([])
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const { open, expanded, focus, seq, size, resetKey } = state
  useEffect(() => {
    if (!open) return undefined
    const token = {}
    escapeStack.push(token)
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || escapeStack[escapeStack.length - 1] !== token) return
      e.stopPropagation()
      onCloseRef.current()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      const at = escapeStack.indexOf(token)
      if (at >= 0) escapeStack.splice(at, 1)
    }
  }, [open])
  useEffect(() => {
    if (!open || focus === null) return
    const el = itemRefs.current[focus]
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }, [open, focus, seq])
  if (!open || !size) return null
  const setExpanded = (fn) => setState((s) => ({ ...s, expanded: fn(s.expanded) }))
  const resetSize = () => {
    clearModalSize(storage())
    setState((s) => ({ ...s, size: defaultModalSize(window.innerWidth, window.innerHeight), resetKey: s.resetKey + 1 }))
  }
  const onResizeStop = (next) => {
    saveModalSize(storage(), next)
    setState((s) => ({ ...s, size: next }))
  }
  // Each block scrolls inside itself past about 60% of the modal's height.
  const blockMaxHeight = Math.max(120, Math.round(size.height * 0.6))
  return (
    <DraggableModal
      key={resetKey}
      open
      onClose={onClose}
      title="API Call Preview"
      testId="view-calls-modal"
      width={size.width}
      height={size.height}
      minWidth={VIEW_CALLS_MIN.width}
      minHeight={VIEW_CALLS_MIN.height}
      centered
      onResizeStop={onResizeStop}
      headerActions={(
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, mr: 0.5 }}>
          <Typography component="span" variant="caption" color="text.secondary" data-testid="view-calls-count">
            {list.length === 1 ? '1 call' : `${list.length} calls`}
          </Typography>
          <Button size="small" data-testid="view-calls-expand-all" onClick={() => setExpanded((s) => toggleExpandAll(s, list.length))}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 0 }}>
            {expandAllText(expanded, list.length)}
          </Button>
          <Link component="button" type="button" underline="hover" data-testid="view-calls-reset-size" onClick={resetSize}
            sx={{ fontSize: 12, color: 'text.secondary' }}>
            Reset size
          </Link>
        </Stack>
      )}
    >
      <Stack spacing={1} data-testid="view-calls-content" sx={{ minWidth: 0 }}>
        {list.map((c, i) => {
          const isOpen = expanded.has(i)
          return (
            <Box key={i} ref={(el) => { itemRefs.current[i] = el }} data-testid="view-calls-item" data-expanded={isOpen ? '1' : '0'}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 0 }}>
              <ButtonBase data-testid="view-calls-item-toggle" aria-expanded={isOpen} onClick={() => setExpanded((s) => toggleExpanded(s, i))}
                sx={{ width: '100%', justifyContent: 'flex-start', gap: 1, px: 1.5, py: 1, textAlign: 'left', borderRadius: 1 }}>
                <Box component="span" aria-hidden sx={{ fontSize: 12, width: 12, color: 'text.secondary' }}>{isOpen ? '▾' : '▸'}</Box>
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, flexGrow: 1, minWidth: 0, ...WRAP }}>{c.label}</Typography>
                {c.call.method && <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{[c.call.service, c.call.method].filter(Boolean).join(' ')}</Typography>}
                {/* found / not found / error (2026-10-29), the Activity Log row's chip in its short form. */}
                <CallStatusChip compact testId="view-calls-item-status" logged={c.logged}
                  call={{ ...c.call, found: c.found, error: c.error, errorMessage: c.errorMessage }} />
              </ButtonBase>
              {isOpen && (
                <Box data-testid="view-calls-item-details" sx={{ px: 1.5, pb: 1.5, minWidth: 0 }}>
                  <ApiCallDetails call={c.call} logged={c.logged} blockMaxHeight={blockMaxHeight} />
                </Box>
              )}
            </Box>
          )
        })}
      </Stack>
    </DraggableModal>
  )
}

// The trigger, the same everywhere. variant="link" (a step's title row,
// Other Actions): "View call" / "View calls (N)" in the primary color at
// weight 600 with the code icon. variant="button" (the Activity Log): a
// small outlined button, "View API call" / "View N API calls".
export function ViewCallsTrigger({ count, onClick, testId, variant = 'link' }) {
  if (variant === 'button') {
    return (
      <Button variant="outlined" size="small" color="primary" data-testid={testId} data-view-calls-trigger="button" onClick={onClick}
        startIcon={<CodeIcon data-testid="view-calls-icon" sx={{ fontSize: '16px !important' }} />}
        sx={{ fontSize: 13, fontWeight: 600, textTransform: 'none', py: 0.125, px: 1, lineHeight: 1.6, flexShrink: 0 }}>
        {activityViewCallsText(count)}
      </Button>
    )
  }
  return (
    <Link component="button" type="button" underline="hover" data-testid={testId} data-view-calls-trigger="link" onClick={onClick}
      sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main', textAlign: 'left', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.5, ...WRAP }}>
      <CodeIcon data-testid="view-calls-icon" sx={{ fontSize: 14 }} />
      {viewCallsLinkText(count)}
    </Link>
  )
}

// An Activity Log call row with detail: click, Enter or Space opens the
// modal at that call; a hover background and a small "View" on the right.
// A row with nothing recorded (CHECKPOINT / NOTE) is not wrapped.
export function ViewCallRow({ onOpen, ariaLabel, children, testId = 'activity-call-row' }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() }
      }}
      sx={{
        display: 'flex', alignItems: 'stretch', gap: 0.5, minWidth: 0, cursor: 'pointer', borderRadius: 1,
        '& > .view-call-row-body > .MuiPaper-root': { transition: 'background-color 120ms' },
        '&:hover > .view-call-row-body > .MuiPaper-root, &:focus-visible > .view-call-row-body > .MuiPaper-root': { bgcolor: 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
        '& > .view-call-row-hint': { opacity: 0, transition: 'opacity 120ms' },
        '&:hover > .view-call-row-hint, &:focus-visible > .view-call-row-hint': { opacity: 1 },
      }}
    >
      <Box className="view-call-row-body" sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      <Box className="view-call-row-hint" aria-hidden data-testid="activity-call-row-view"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, fontSize: 12, fontWeight: 600, color: 'primary.main', pr: 0.5 }}>
        <CodeIcon sx={{ fontSize: 13 }} />View
      </Box>
    </Box>
  )
}

// calls: [{ label, call, logged? }] (a call that follows the step's current inputs
// is computed by the caller at render). title: the step's title, the label
// of a lone unlabeled call.
export function ViewCalls({ calls = [], title = null, testId = 'agent-draft-step-view-call', variant = 'link' }) {
  const list = viewCallEntries(calls, { title })
  const { openAt, modal } = useViewCallsModal(list)
  if (!list.length) return null
  return (
    <>
      <ViewCallsTrigger count={list.length} variant={variant} testId={testId} onClick={() => openAt()} />
      {modal}
    </>
  )
}
